import { z } from "zod";
import type { RoleCode } from "../auth";
import {
	COMPETITION_CODE_PATTERN,
	type CompetitorStatus,
	type MemberRole,
	POOL_PATTERN,
	SIDE_PATTERN,
} from "./vocab";

/**
 * What every competition slice needs and none of them owns: who may call what,
 * the field rules that repeat on every form, and the response shapes that show
 * a competitor. They live here so that four slices built in parallel do not
 * each invent their own — and then disagree about what a code, a pool or a
 * competitor's name is.
 */

// ─── Who may do what ─────────────────────────────────────────────────────────
//
// Authorization reads the session's ONE active role (CLAUDE.md, auth
// invariants), so each list is simply the roles a route accepts:
// `requireRole([...COMPETITION_READ_ROLES])`. On top of the role, an OPERATION
// write is limited to its user group's sports and permissions, checked in the
// writing statement (`eventWritableBy`, apps/api/src/db/queries/sport-access.ts).

/**
 * Every staff role that reads competition data — schedules, start lists,
 * results, medals: the GMS staff roles, and the medal desk (MSES reads the
 * events it awards and the schedule).
 */
export const COMPETITION_READ_ROLES = ["SUPER_ADMIN", "ADMIN", "MEDAL_DESK", "OPERATION", "ORGANIZATION"] as const satisfies readonly RoleCode[];

/** The host's administrators: they define the programme — sports, disciplines, events, venues. */
export const COMPETITION_SETUP_ROLES = ["SUPER_ADMIN", "ADMIN"] as const satisfies readonly RoleCode[];

/**
 * The people who RUN a competition — OVR operators, plus the administrators:
 * phases, meets, competitors, start lists, results. An ORGANIZATION login (an
 * NOC) reads these and never writes them.
 */
export const COMPETITION_WRITE_ROLES = ["SUPER_ADMIN", "ADMIN", "OPERATION"] as const satisfies readonly RoleCode[];

/**
 * The medal desk (MSES) — the ONLY role that awards, changes or takes away a
 * medal, locks or unlocks an event, or sets its medal ceremony time (decided
 * by the user 2026-10-22). Not SUPER_ADMIN, not ADMIN: the same
 * administrators run OVR, and OVR only SUGGESTS a podium
 * (`PUT /events/:id/ranking`). An administrator who awards also holds
 * MEDAL_DESK and signs in to MSES with it; OVR refuses a MEDAL_DESK session
 * (`apps/ovr/src/lib/auth/access.ts`), so no OVR session can award.
 * Authorization still reads the ONE active role and nothing else (CLAUDE.md,
 * auth invariants).
 */
export const MEDAL_AWARD_ROLES = ["MEDAL_DESK"] as const satisfies readonly RoleCode[];

/**
 * Who reads the desk's "no medals" decisions (`GET /medals/no-medals`): the
 * desk and the administrators. Not the other competition readers — each row
 * carries the desk's own reason (a protest, a ruling), which is not for an
 * NOC or an operator to read.
 */
export const MEDAL_DECISION_READ_ROLES = ["SUPER_ADMIN", "ADMIN", "MEDAL_DESK"] as const satisfies readonly RoleCode[];

// ─── Field rules ─────────────────────────────────────────────────────────────

export interface CompetitionFieldMessages {
	codeRequired: string;
	/** Letters, digits, `-` and `_`; starts with a letter or a digit; 20 characters at most. */
	codeFormat: string;
	/** 1 to 4 letters or digits. */
	poolFormat: string;
	/** Letters, digits and `_`; 12 characters at most. */
	sideFormat: string;
}

export const enCompetitionFieldMessages: CompetitionFieldMessages = {
	codeRequired: "Enter a code",
	codeFormat: "Letters, numbers, - and _ only, starting with a letter or a number (20 at most)",
	poolFormat: "1 to 4 letters or numbers",
	sideFormat: "Letters, numbers and _ only (12 at most)",
};

/**
 * The same pattern, matched without regard to case.
 *
 * Built FROM the `*_PATTERN` constant, never typed beside it: the constant is
 * what the column's CHECK constraint is generated from, so a form rule derived
 * from it cannot drift from the database.
 *
 * Two shortcuts this deliberately does not take. No `u` flag: with it, `i`
 * folds "ſ" and the Kelvin sign onto "s" and "k", and they would pass as
 * letters. And not "upper-case first, then test the pattern": "ß" upper-cases
 * to "SS" and "ı" to "I", so they would pass too and be stored as something
 * the user never typed. Checked first, in any case, then upper-cased — only
 * ASCII ever gets through.
 */
const anyCase = (pattern: RegExp): RegExp => new RegExp(pattern.source, "i");

const CODE_ANY_CASE = anyCase(COMPETITION_CODE_PATTERN);
const POOL_ANY_CASE = anyCase(POOL_PATTERN);
const SIDE_ANY_CASE = anyCase(SIDE_PATTERN);

/**
 * `code` on sports, disciplines, events and venues — AQ, SWM, M100FR, NAC-C1.
 * Typed in any case, trimmed, checked, then STORED UPPER-CASE, so a lookup
 * never has to case-fold (the same shape as `functionCodeField`).
 *
 * What comes out always satisfies `COMPETITION_CODE_PATTERN`, which is the
 * CHECK on those four columns: the check here is that pattern ignoring case,
 * and upper-casing an ASCII match cannot leave it.
 */
export const makeCompetitionCodeField = (
	m: Pick<CompetitionFieldMessages, "codeRequired" | "codeFormat"> = enCompetitionFieldMessages,
) =>
	z
		.string()
		.trim()
		.min(1, m.codeRequired)
		.regex(CODE_ANY_CASE, m.codeFormat)
		.transform((v) => v.toUpperCase());
export const competitionCodeField = makeCompetitionCodeField();

/**
 * A pool / group label on a meet or a phase entry — A, B, 1, A2. Same rule as
 * the code: any case in, upper-case stored, and the output satisfies
 * `POOL_PATTERN` (the CHECK on `meets.pool` and `phase_competitors.pool`).
 *
 * Both sides of a link must go through this field: "1st of group a" finds
 * nobody if the group was saved as "A".
 */
export const makePoolField = (m: Pick<CompetitionFieldMessages, "poolFormat"> = enCompetitionFieldMessages) =>
	z
		.string()
		.trim()
		.regex(POOL_ANY_CASE, m.poolFormat)
		.transform((v) => v.toUpperCase());
export const poolField = makePoolField();

/**
 * Which side of a head-to-head a slot takes — HOME, AWAY, RED, BLUE. Any case
 * in, upper-case stored; the output satisfies `SIDE_PATTERN` (the CHECK on
 * `meet_competitors.side`).
 */
export const makeSideField = (m: Pick<CompetitionFieldMessages, "sideFormat"> = enCompetitionFieldMessages) =>
	z
		.string()
		.trim()
		.regex(SIDE_ANY_CASE, m.sideFormat)
		.transform((v) => v.toUpperCase());
export const sideField = makeSideField();

/**
 * The platform's ten languages. `bm` is THIS platform's code for Malay — not
 * ISO `ms`, and not Bambara (CLAUDE.md, i18n).
 *
 * The web keeps its own copy (`SUPPORTED_LOCALES` in
 * `apps/gms/src/lib/i18n/locales.ts`) and should import this one when the GMS
 * sports pages are built; until then the two lists are typed twice and nothing
 * but a reader keeps them equal.
 */
export const LOCALE_CODES = ["en", "bm", "id", "th", "vi", "fil", "my", "km", "lo", "tet"] as const;
export type LocaleCode = (typeof LOCALE_CODES)[number];

/** The locales a name can be translated INTO: every one except English, because `name` is the English text. */
export type TranslationLocale = Exclude<LocaleCode, "en">;
export const TRANSLATION_LOCALES: readonly TranslationLocale[] = LOCALE_CODES.filter(
	(code): code is TranslationLocale => code !== "en",
);

const translationText = z.string().trim().min(1).max(200);

/**
 * `name_translations` on sports, disciplines and events: `{ "bm": "Akuatik",
 * "th": "…" }`. English is not a key — the `name` column is the English master
 * — so sending `en` is refused rather than silently stored and never read.
 *
 * FIXED keys, one per locale, built from `LOCALE_CODES`; `.strict()`, and not
 * `z.record`. Two reasons. A typo'd locale must be a 422, not a translation
 * nobody will ever see. And the API's `ok()` converts every response key to
 * snake_case through a cache that grows by one entry per distinct key: keys
 * that come from user input would let a client grow it without limit. Locale
 * codes are already lower-case, so they pass through that conversion unchanged.
 *
 * A locale is cleared by leaving it out: the object is stored whole.
 */
export const nameTranslationsField = z
	.object(
		Object.fromEntries(TRANSLATION_LOCALES.map((code) => [code, translationText.optional()])) as Record<
			TranslationLocale,
			z.ZodOptional<typeof translationText>
		>,
	)
	.strict();
export type NameTranslations = z.infer<typeof nameTranslationsField>;

/** The largest value a Postgres `integer` column holds. */
const INT4_MAX = 2_147_483_647;

/**
 * Optimistic locking: the `version` the client LOADED, sent back with its
 * change. The write goes through only if the row still has it; otherwise the
 * answer is 409 `STALE_VERSION` and nothing is written — two operators cannot
 * silently overwrite each other.
 *
 * Capped at what the column can hold, so a nonsense version is a 422 from the
 * validator and not an "integer out of range" error from Postgres.
 */
export const versionField = z.number().int().min(1).max(INT4_MAX);

/**
 * A client-made id on a create (`meet_id`, `competitor_id`, …). Sending it
 * makes the create repeatable: the same request twice returns the existing row
 * and writes nothing, which is what a retry after a dropped connection — and
 * later an offline venue — needs.
 *
 * Lower-cased here because Postgres prints uuids in lower case: a client that
 * sends "AB12…" would otherwise get back an id that no longer equals the one
 * it sent, and any comparison of the two as strings would miss.
 */
export const clientIdField = z.string().uuid().toLowerCase().optional();

// ─── Day ranges and list parameters ──────────────────────────────────────────
//
// Shared by the schedule (`meets.ts`) and the medal board (`medals.ts`): one
// definition of a games day and of a comma-separated list parameter.

/** `0000` is the one 4-digit year Postgres refuses (there is no year zero): a 422 here, not a 500 there. */
export const competitionNotYearZero = (v: string): boolean => !v.startsWith("0000");

/** `A,B , C` → `["A", "B", "C"]`: a comma-separated query parameter, blanks dropped. */
export const competitionSplitList = (v: string): string[] =>
	v
		.split(",")
		.map((s) => s.trim())
		.filter((s) => s.length > 0);

/** A calendar day, `YYYY-MM-DD` — a real one (no 30 February). */
export const competitionDayField = z.string().date().refine(competitionNotYearZero, "Invalid date");

/**
 * `from` / `to` are DAYS in the games' own `timezone` (never the server's,
 * never the viewer's): `from` is inclusive, `to` exclusive. One day is
 * `from=2027-12-05&to=2027-12-06`. A meet at 16:30 UTC on the 4th starts at
 * 00:30 on the 5th in Kuala Lumpur, so it is ON the 5th.
 */
export const competitionDayRangeShape = { from: competitionDayField.optional(), to: competitionDayField.optional() };

/** `.superRefine()` for a day range: `to` must be after `from` when both are given. */
export const competitionFromBeforeTo = (v: { from?: string | undefined; to?: string | undefined }, ctx: z.RefinementCtx): void => {
	if (v.from !== undefined && v.to !== undefined && v.to <= v.from) {
		ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["to"], message: "`to` must be after `from`" });
	}
};

// ─── Response shapes ─────────────────────────────────────────────────────────
//
// Wire shapes (snake_case). Every endpoint that shows a competitor shows THESE,
// built by the SQL fragments in `apps/api/src/db/queries/competition-shared.ts`
// — one definition of a competitor's name, not one per endpoint.

/** The organization a competitor competes for. `code` is the country's IOC code when the organization has a country. */
export interface NocRef {
	responsible_organization_id: string;
	name: string;
	code: string | null;
	flag_url: string | null;
	/** The country's common name — "Cambodia", where `name` is "NOC Cambodia". Null for an organization with no country. */
	country_name: string | null;
}

export interface CompetitorRef {
	competitor_id: string;
	entry_no: number;
	/**
	 * The DISPLAY name, never empty for a real competitor: the team's stored
	 * name; else the athlete's name, or both names of a pair; else the
	 * organization's name (numbered when it has several entries in the event).
	 */
	name: string;
	status: CompetitorStatus;
	noc: NocRef;
}

/** One person on a competitor's roster. No identity numbers, contacts or dates of birth — a name and a role. */
export interface CompetitorMemberRef {
	competitor_member_id: string;
	people_id: string;
	name: string;
	role: MemberRole;
	bib: string | null;
	is_captain: boolean;
	sort_order: number;
	sport_class_code: string | null;
}
